import React, { useState, useEffect } from "react";
import { Card, CardMedia, Box, Typography, Tooltip, Avatar } from "@mui/material";
import { Link } from "react-router-dom";
import { Host } from "../models/hostModel";

import HostCardPendingApprovalOverlay from "./HostCardPendingApprovalOverlay";
import HostCardFailedPaymentOverlay from "./HostCardFailedPaymentOverlay";
import {
  HeartIcon,
  PremiumIcon,
  WheelchairIcon,
  FamilyRestroomIcon,
  MenBabyChangingIcon,
  WomenBabyChangingIcon,
  HandSanitizerIcon,
  SeatCoversIcon,
  WetWipeIcon,
  PetFriendlyIcon,
} from "../components/Icons/Index";
import googleAPIService from "services/googleAPIService";
import { DEFAULT_BUILDING_IMAGE } from "../constants";

interface HostCardProps {
  id: any;
  key: any;
  host: Host;
  imageSize: "small" | "large";
  largeDisplay?: boolean;
  hideActionButton?: boolean;
  hideOpenStatus?: boolean;
  onHostSelect?: (host: Host) => void;
  onUpgradeToPremiumClick?: () => void;
  isViewedOnHostPortal?: boolean;
  hideFavoriteIcon?: boolean;
  isMaintenanceMode?: boolean;
  onApproveHost?: (id: number, isApproved: boolean) => void;
  onSetTestHost?: (id: number, isTest: boolean) => void;
  onSetFreeForever?: (id: number, isFreeForever: boolean) => void;
}

const HostCard = (props: HostCardProps) => {
  const {
    id,
    key,
    host,
    imageSize,
    largeDisplay,
    hideActionButton,
    onHostSelect,
    hideOpenStatus,
    onUpgradeToPremiumClick,
    isViewedOnHostPortal,
    hideFavoriteIcon,
    isMaintenanceMode,
    onApproveHost,
    onSetTestHost,
    onSetFreeForever,
  } = props;
  const [isFavorite, setIsFavorite] = useState(host.isFavorite);
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [showIconLegend, setShowIconLegend] = useState(false);
  const [hostPicture, setHostPicture] = useState<any>();

  const toggleFavorite = () => {
    setIsFavorite((prev) => !prev);
    // Call your favorite API logic here if needed
  };
  useEffect(() => {
    getHostPictures();
  }, []);
  const setHostPictureSource = () => {
    if (hostPicture && hostPicture.length > 0) return hostPicture;
    else return host.imageuri ? host.imageuri : DEFAULT_BUILDING_IMAGE;
  };
  const handleClick = () => {
    if (onHostSelect) onHostSelect(host);
  };

  const getHostPictures = () => {
    googleAPIService.getPlacePictures(host.placeId).then((result) => {
      if (!result) {
        //no images available in places api - try to get a street view
        googleAPIService.getStreetViewPictureURL(host.latitude, host.longitude).then((result2) => {
          setHostPicture(result2);
        });
      } else {
        setHostPicture(result);
      }
    });
  };

  const openStatusText = () => {
    if (!host.isApproved) return "Pending Admin Approval!";
    if (host.closedForCleaning) return "Closed for Cleaning";
    if (host.is24Hours)
      return host.currentQue > 0
        ? `Open 24 Hours! - ${host.currentQue} in line`
        : "Open 24 Hours! - No line!";

    if (host.closedToday)
      return `Closed on ${new Date().toLocaleString("en-US", { weekday: "long" })}`;

    if (host.currentlyOpen)
      return host.currentQue > 0 ? `Open - ${host.currentQue} in line` : "Open - No line!";

    return `Closed at ${host.closeTimeToday}`;
  };

  const statusColor = () => {
    if (!host.isApproved || host.closedForCleaning || host.closedToday || !host.currentlyOpen)
      return { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
    if (host.currentlyOpen) return { background: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
    return { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: "#bfdbfe",
          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.10)",
        },
      }}
      onClick={handleClick}
    >
      {!isViewedOnHostPortal && !host.isApproved && <HostCardPendingApprovalOverlay />}
      {host.failedPaymentId && <HostCardFailedPaymentOverlay />}

      {/* Host Image */}
      <CardMedia
        component="img"
        height="168"
        image={setHostPictureSource()}
        alt={host.name}
        sx={{ objectFit: "cover", backgroundColor: "grey.100" }}
      />

      <Box p={2.25} display="flex" flexDirection="column" gap={1.25} flex={1}>
        {/* Top row: type and favorite */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: "0.025em" }}
          >
            {host.hostPremiumOnly ? "Premium Hosting" : "Standard Hosting"}
          </Typography>
          {!hideFavoriteIcon && (
            <HeartIcon
              name={isFavorite ? "heart" : "heart-o"}
              color={isFavorite ? "red" : "black"}
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite();
              }}
            />
          )}
        </Box>

        {/* Host name */}
        <Typography variant="h6" sx={{ fontSize: "1.05rem", lineHeight: 1.35, fontWeight: 700 }}>
          {host.name}
        </Typography>

        {/* Address */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            lineHeight: 1.45,
            display: "block", // ensures proper wrapping
            whiteSpace: "normal", // prevents forced extra spacing
          }}
        >
          {host.address}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            lineHeight: 1.45,
            display: "block", // ensures proper wrapping
            whiteSpace: "normal", // prevents forced extra spacing
          }}
        >
          {host.city}, {host.state} {host.zip}
        </Typography>

        {/* Open status */}
        <Box
          sx={{
            backgroundColor: statusColor().background,
            color: statusColor().color,
            border: `1px solid ${statusColor().border}`,
            borderRadius: 99,
            padding: "6px 10px",
            display: "inline-flex",
            alignSelf: "flex-start",
            marginTop: "auto",
          }}
        >
          <Typography variant="caption" sx={{ lineHeight: 1.2, fontWeight: 700, color: "inherit" }}>
            {openStatusText()}
          </Typography>
        </Box>

        {/* Amenities */}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={1.25}
          mt={0.5}
          color="text.secondary"
        >
          {host.hostPremiumOnly && <PremiumIcon />}
          {host.offerHandicapAccessible && <WheelchairIcon />}
          {host.offerFamilyRestroom && <FamilyRestroomIcon />}
          {host.offerMenBabyChaningTable && <MenBabyChangingIcon />}
          {host.offerWomenBabyChaningTable && <WomenBabyChangingIcon />}
          {host.offerWomenProducts && <Typography variant="caption">♀</Typography>}
          {host.offerMenProducts && <Typography variant="caption">♂</Typography>}
          {host.offerHandSanitizer && <HandSanitizerIcon />}
          {host.offerSeatCovers && <SeatCoversIcon />}
          {host.offerWetWipes && <WetWipeIcon />}
          {host.offerPetFriendly && <PetFriendlyIcon />}
          {host.offerAutoCharger && <Typography variant="caption">⚡</Typography>}
          {host.overnightFriendly && <Typography variant="caption">🏕️</Typography>}
          {host.motorcycleFriendly && <Typography variant="caption">🏍️</Typography>}
          {host.offerLuxury && <Typography variant="caption">💎</Typography>}
        </Box>
      </Box>
    </Card>
  );
};

export default HostCard;
